import type { PromptsTableProps } from "../types";

import React from "react";
import { useTranslation } from "react-i18next";
import {
  Table,
  TableHeader,
  TableBody,
  TableColumn,
  TableRow,
  TableCell,
  Button,
  Chip,
} from "@heroui/react";
import { Icon } from "@iconify/react";

import { LanguageBadge } from "@/components/Prompt/LanguageBadge";
import { useTheme } from "@/hooks/use-theme";

import { CollapsibleContent } from "./CollapsibleContent";

export const PromptsTable: React.FC<PromptsTableProps> = ({
  prompts,
  onEdit,
  onDelete,
  isTablet = false,
}) => {
  const { t } = useTranslation();
  const { theme } = useTheme();

  const renderTableContent = () => (
    <Table aria-label="Prompts table" data-id="prompts-table">
      <TableHeader>
        <TableColumn data-id="prompts-column-name">{t("Name")}</TableColumn>
        <TableColumn data-id="prompts-column-description" width="30%">
          {t("Description")}
        </TableColumn>
        <TableColumn data-id="prompts-column-content" width="30%">
          {t("Content")}
        </TableColumn>
        <TableColumn data-id="prompts-column-language" width="10%">
          {t("Language")}
        </TableColumn>
        <TableColumn data-id="prompts-column-tags" width="10%">
          {t("Tags")}
        </TableColumn>
        <TableColumn data-id="prompts-column-public">{t("Public")}</TableColumn>
        <TableColumn data-id="prompts-column-actions">{t("Actions")}</TableColumn>
      </TableHeader>
      <TableBody>
        {prompts.map((prompt) => (
          <TableRow
            key={prompt.id}
            className="border-b border-brand-primary last:border-b-0"
            data-id={`prompt-row-${prompt.id}`}
          >
            <TableCell data-id={`prompt-name-${prompt.id}`}>{prompt.name}</TableCell>
            <TableCell data-id={`prompt-description-${prompt.id}`}>
              {prompt.description ? (
                <CollapsibleContent content={prompt.description} maxLength={60} />
              ) : (
                <span className="text-gray-400 italic">No description</span>
              )}
            </TableCell>
            <TableCell
              className={isTablet ? "max-w-xs truncate" : "max-w-md truncate"}
              data-id={`prompt-content-${prompt.id}`}
            >
              <CollapsibleContent content={prompt.content} maxLength={120} />
            </TableCell>
            <TableCell data-id={`prompt-language-${prompt.id}`}>
              <LanguageBadge
                dataId={`prompt-language-badge-${prompt.id}`}
                sourceLanguage={prompt.sourceLanguage || "en-GB"}
              />
            </TableCell>
            <TableCell data-id={`prompt-tags-${prompt.id}`}>
              <div className="flex flex-wrap gap-1">
                {prompt.tags &&
                  prompt.tags.map((tag, index) => (
                    <Chip
                      key={index}
                      data-id={`prompt-tag-${prompt.id}-${index}`}
                      size="sm"
                      variant="flat"
                    >
                      {tag}
                    </Chip>
                  ))}
              </div>
            </TableCell>
            <TableCell data-id={`prompt-public-${prompt.id}`}>
              {prompt.isPublic ? (
                <Chip
                  className={!isTablet ? "bg-brand-primary text-white" : ""}
                  color={isTablet ? "success" : undefined}
                  data-id={`prompt-public-status-${prompt.id}`}
                  size="sm"
                >
                  {t("Public")}
                </Chip>
              ) : (
                <Chip
                  classNames={
                    !isTablet
                      ? {
                          base:
                            theme === "light"
                              ? "border-brand-primary text-brand-primary"
                              : "border-white text-white",
                        }
                      : undefined
                  }
                  color={isTablet ? "warning" : undefined}
                  data-id={`prompt-private-status-${prompt.id}`}
                  size="sm"
                  variant={!isTablet ? "bordered" : undefined}
                >
                  {t("Private")}
                </Chip>
              )}
            </TableCell>
            <TableCell data-id={`prompt-actions-${prompt.id}`}>
              <div className="flex gap-2">
                <Button
                  isIconOnly
                  color="primary"
                  data-id={`prompt-edit-${prompt.id}`}
                  size={isTablet ? "sm" : undefined}
                  variant="light"
                  onPress={() => onEdit(prompt)}
                >
                  <Icon className={isTablet ? "h-4 w-4" : "h-5 w-5"} icon="mdi:pencil" />
                </Button>
                <Button
                  isIconOnly
                  color="danger"
                  data-id={`prompt-delete-${prompt.id}`}
                  size={isTablet ? "sm" : undefined}
                  variant="light"
                  onPress={() => onDelete(prompt)}
                >
                  <Icon className={isTablet ? "h-4 w-4" : "h-5 w-5"} icon="mdi:delete" />
                </Button>
              </div>
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );

  if (isTablet) {
    return (
      <div className="hidden sm:block lg:hidden overflow-x-auto" data-id="prompts-table-tablet">
        <div className="min-w-[800px]">{renderTableContent()}</div>
      </div>
    );
  }

  return (
    <div className="hidden lg:block" data-id="prompts-table-desktop">
      {renderTableContent()}
    </div>
  );
};
